import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, TextField, Stack, Button, useTheme, Alert } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axios from 'axios';
import { diff_match_patch } from 'diff-match-patch';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function ApiResponseDiff() {
  const theme = useTheme();
  const [url1, setUrl1] = useState('https://jsonplaceholder.typicode.com/users/1');
  const [url2, setUrl2] = useState('https://jsonplaceholder.typicode.com/users/2');
  const [res1, setRes1] = useState('');
  const [res2, setRes2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFetchAndCompare = async () => {
    if (!url1 || !url2) {
      setError("Please enter both URLs.");
      return;
    }
    setError(null);
    setLoading(true);

    try {
      const [response1, response2] = await Promise.all([
        axios.get(url1, { timeout: 10000 }).catch(e => e.response || { data: `Error: ${e.message}` }),
        axios.get(url2, { timeout: 10000 }).catch(e => e.response || { data: `Error: ${e.message}` })
      ]);

      const formatted1 = typeof response1.data === 'object' ? JSON.stringify(response1.data, null, 2) : String(response1.data);
      const formatted2 = typeof response2.data === 'object' ? JSON.stringify(response2.data, null, 2) : String(response2.data);

      setRes1(formatted1);
      setRes2(formatted2);
    } catch (e) {
      setError("An unexpected error occurred while fetching.");
    } finally {
      setLoading(false);
    }
  };

  const diffHtml = useMemo(() => {
    if (!res1 && !res2) return '';
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(res1, res2);
    dmp.diff_cleanupSemantic(diff);
    return dmp.diff_prettyHtml(diff);
  }, [res1, res2]);

  const stats = useMemo(() => {
    if (!res1 && !res2) return { additions: 0, deletions: 0 };
    const dmp = new diff_match_patch();
    const diff = dmp.diff_main(res1, res2);
    const additions = diff.filter(part => part[0] === 1).length;
    const deletions = diff.filter(part => part[0] === -1).length;
    return { additions, deletions };
  }, [res1, res2]);

  return (
    <>
      <SEO
        title="API Response Diff Tool | Compare REST APIs | UmbrellaCo"
        description="Fetch and compare responses from two different REST API endpoints instantly. Highlight differences between staging and production environments."
        keywords="api response diff, compare api response, rest api diff, staging vs production diff, compare json responses"
        url="/api-tools/response-diff"
        jsonLd={createSoftwareAppSchema(
          'API Response Diff Tool',
          'Fetch and compare responses from two REST API endpoints simultaneously.',
          'https://umbrellaco.in/api-tools/response-diff',
          ['API Diff', 'REST Testing', 'Environment Comparison']
        )}
      />
      <ToolPageContainer
        title="API Response Diff Tool"
        description="Enter two API endpoint URLs (e.g., Staging vs Production) to fetch and visually compare their JSON responses. Note: CORS restrictions apply."
      >

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="API URL 1 (Base/Staging)"
            value={url1}
            onChange={(e) => setUrl1(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
          />
          <CompareArrowsIcon color="action" />
          <TextField
            label="API URL 2 (Target/Production)"
            value={url2}
            onChange={(e) => setUrl2(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
          />
          <Button 
            variant="contained" 
            onClick={handleFetchAndCompare} 
            disabled={loading}
            sx={{ minWidth: 150 }}
          >
            {loading ? 'Fetching...' : 'Fetch & Compare'}
          </Button>
        </Stack>
      </Paper>

      {res1 || res2 ? (
        <Paper sx={{ mt: 2, p: 2, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, flexGrow: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
            <CompareArrowsIcon color="primary" />
            <Typography>
              <Typography component="span" sx={{ color: theme.palette.success.main, fontWeight: 'bold' }}>{stats.additions} Additions</Typography>
              {' / '}
              <Typography component="span" sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>{stats.deletions} Deletions</Typography>
            </Typography>
          </Stack>
          <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
            <Paper
              variant="outlined"
              sx={{ p: 2, fontSize: '1rem', lineHeight: 1.7, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
              dangerouslySetInnerHTML={{ __html: diffHtml }}
            />
          </Box>
        </Paper>
      ) : (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">Enter URLs and click "Fetch & Compare" to see the diff here.</Typography>
        </Box>
      )}
      </ToolPageContainer>
    </>
  );
}

export default ApiResponseDiff;
